import type { Ability, Skill, SkillRequirement } from '../types';

/**
 * RO-A data conversion (abilities persist v7 AND every cloud load/merge).
 *
 * ⚠️ RECORDS ONLY (owner ruling 1). It adds requirement `pointRecord`s, sets `xp`
 * from them, and adds the legacy bonus marker. It never recomputes a skill's
 * xp/progress/completedAt or an ability's xp/level/progress — those change on the
 * next real action, as before — so nobody's ability level moves just by loading.
 *
 * - A requirement that already has a record keeps it untouched; its `xp` is set to
 *   the record's points (an old open tab may have zeroed or re-minted it).
 * - A record-less COMPLETED requirement with a positive finite `xp` is recorded at
 *   exactly that amount — never revalued at today's level.
 * - A record-less UNFINISHED requirement pays nothing: `xp` becomes 0.
 * - A skill flagged `completionBonusAwarded` without a `bonusRecord` keeps only the
 *   bonus still evidenced by its stored xp (skill.xp − its completed requirements'
 *   xp, floored at 0). The old recompute usually dropped it, so this is often a
 *   zero-point marker: it pays nothing and only blocks a second award.
 *
 * Idempotent: a second run changes nothing and returns the same references.
 */
export interface ConversionContext { now: string; userLevel: number; touch: (ability: Ability) => string }

const positive = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0;

function convertRequirement(req: SkillRequirement, ctx: ConversionContext): SkillRequirement {
  if (req.pointRecord) {
    return req.xp === req.pointRecord.points ? req : { ...req, xp: req.pointRecord.points };
  }
  if (req.completed && positive(req.xp)) {
    return { ...req, pointRecord: { points: req.xp, userLevel: ctx.userLevel, earnedAt: req.completedAt ?? ctx.now } };
  }
  return req.xp === undefined || req.xp === 0 ? req : { ...req, xp: 0 };
}

function convertSkill(skill: Skill, ctx: ConversionContext): Skill {
  const requirements = skill.requirements ?? [];
  const converted = requirements.map(req => convertRequirement(req, ctx));
  const requirementsChanged = converted.some((req, i) => req !== requirements[i]);
  let bonusRecord = skill.bonusRecord;
  if (skill.completionBonusAwarded && !bonusRecord) {
    const legacyRequirementXP = requirements.reduce((sum, req) => sum + (req.completed && positive(req.xp) ? req.xp : 0), 0);
    const residual = Math.max(0, Math.round((positive(skill.xp) ? skill.xp : 0) - legacyRequirementXP));
    bonusRecord = { points: residual, userLevel: ctx.userLevel, earnedAt: skill.completedAt ?? ctx.now };
  }
  if (!requirementsChanged && bonusRecord === skill.bonusRecord) return skill;
  return { ...skill, requirements: requirementsChanged ? converted : requirements, ...(bonusRecord ? { bonusRecord } : {}) };
}

export function convertLegacyAbilities(abilities: Ability[], ctx: ConversionContext): Ability[] {
  let changed = false;
  const next = abilities.map(ability => {
    const skills = ability.skills ?? [];
    const converted = skills.map(skill => convertSkill(skill, ctx));
    if (converted.every((skill, i) => skill === skills[i])) return ability;
    changed = true;
    return { ...ability, skills: converted, updatedAt: ctx.touch(ability) };
  });
  return changed ? next : abilities;
}
